import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { BaseDimensionElementCreator } from '@3d-scene/layer/creator/base-dimension-element-creator';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUtils } from '@article/util/article-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ScreenShotService } from '@3d-scene/service/screen-shot.service';
import { ProjectDimensionsService } from '@project/components/project/service/project-dimensions.service';
import { Group, MeshBasicMaterial, Vector3 } from 'three';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { DimensionElement } from '@3d-scene/layer/elements/dimension-element';
import { ILayerElement } from '@3d-scene/layer/elements/i-layer-element';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { ContainerService } from '../../model3d/container.service';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { SimpleTextValueVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { DimensionUtils } from '@3d-scene/layer/util/dimension.utils';
import { LabelElement } from '@3d-scene/layer/elements/label-element';
import { DimensionDescriptor } from '@3d-scene/layer/util/dimension-descriptor';
import { DimensionLevelMetricsUtil } from '@3d-scene/layer/util/dimension-level-metrics.util';
import { Dictionary } from 'typescript-collections';
import { DimensionMetrics } from '@3d-scene/layer/util/dimension-metrics';
import { PointerLabelElement } from '@3d-scene/layer/elements/pointer-label-element';
import { DrillingHoleDimensionElement } from '@3d-scene/layer/elements/drilling-hole-dimension-element';
import { ImagePlaneElement } from '@3d-scene/layer/elements/image-plane-element';
import { ArticleContainerUtils } from '../../model3d/util/article-container-utils';
import { SceneManager } from '../../manager/scene.manager';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectModel } from '@project/model/project.model';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { ArticleUsageService } from '@article/service/article-usage.service';

export class DrillingHolesLayerElementCreator extends BaseDimensionElementCreator {
  protected static readonly SIZE_EPSILON: number = 0.85;
  protected static readonly OFFSET_FROM_MOUNTING_RAIL: number = 325;
  protected static readonly SCREENSHOT_PADDING: number = 90;

  protected material: MeshBasicMaterial = new MeshBasicMaterial({color: 0x000000});

  constructor(
    containerService: ContainerService,
    localeResourceManager: LocaleResourceManager,
    projectModel: ProjectModel,
    sceneManager: SceneManager,
    private mountingRailHelper: MountingRailHelper,
    articleContainerUtils: ArticleContainerUtils,
    private articleUsageService: ArticleUsageService,
    private projectDimensionsService: ProjectDimensionsService,
    private screenShotService: ScreenShotService,
  ) {
    super(projectModel, localeResourceManager, sceneManager, containerService, articleContainerUtils, 'drillingHoles');
  }

  public createLayerElements(): Group {
    const mountingRail: ArticleContainer = this.mountingRailHelper.getMountingRailContainer();

    let elements: ILayerElement[] = [];
    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    const drillingHoleContainers: ArticleContainer[] = this.getDrillingHoleContainers();

    const sortedDrillingHoleContainers: ArticleContainer[] = drillingHoleContainers.sort((a, b) => this.articlePositionCompareFunction(a, b));

    const maxZ: number = this.findMaxZValue();
    const startPoint: Vector3 = new Vector3(insertHelper.startPoint.x, insertHelper.startPoint.y + 5, maxZ);
    const endPoint: Vector3 = new Vector3(insertHelper.endPoint.x, insertHelper.endPoint.y + 5, maxZ);
    const descriptors: DimensionDescriptor[] = this.collectDimensionDescriptors(startPoint, endPoint, sortedDrillingHoleContainers);
    const maxSizeLevel: number = this.processDimensionDescriptors(descriptors, elements);
    const commonDimension: DimensionElement = this.createCommonDimensionElement(startPoint, endPoint, maxSizeLevel);
    elements.push(commonDimension);
    const endDescriptors: DimensionDescriptor[] = this.collectStartEndDimensionDescriptors(
      startPoint,
      endPoint,
      sortedDrillingHoleContainers,
      this.updateMinLevelValueByDescriptors(descriptors)
    );
    elements.push(...this.createStartEndDimensionElements(endDescriptors));
    elements.push(...this.createFrontViewElements(mountingRail));
    elements.push(this.createZeroLabel(startPoint, maxSizeLevel));

    this.processConnectedContainers(drillingHoleContainers, elements);
    return this.createLayerGroup(elements);
  }

  override onActivate(): void {
    this.changeArticlesVisibility(false);
    this.sceneManager.checkAndMoveCameraToDefaultPositionBeforeDrillingHolesView(true);
  }

  override onDeactivate(): void {
    this.changeArticlesVisibility(true);
    this.sceneManager.checkAndMoveCameraToDefaultPositionBeforeDrillingHolesView(false);
  }

  private processConnectedContainers(drillingHoleContainers: ArticleContainer[], elements: ILayerElement[]): void {
    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    const labelsMap: Map<ArticleContainer, string[]> = this.collectLabels(drillingHoleContainers);

    labelsMap.forEach((labels: string[], articleContainer: ArticleContainer) => {
      const pointerPosition: Vector3 = insertHelper.startPoint.clone();
      pointerPosition.y += 5;
      pointerPosition.x = this.calculateOffset(articleContainer);

      const initLabelPosition: Vector3 = pointerPosition.clone();
      initLabelPosition.y += 5;
      initLabelPosition.z -=
        DrillingHolesLayerElementCreator.OFFSET_FROM_MOUNTING_RAIL + DrillingHolesLayerElementCreator.OFFSET_FROM_MOUNTING_RAIL / 2;

      labels.forEach((label: string, index: number) => {
        const labelPosition: Vector3 = initLabelPosition.clone();
        labelPosition.x -= LabelElement.DEFAULT_LABEL_WIDTH * index;
        const textValue: SimpleTextValueVO = this.createArticleNumberTextValue(label);
        const element: PointerLabelElement = new PointerLabelElement(
          labelPosition,
          pointerPosition,
          textValue,
          this.longestLabelLength,
          index === 0
        );
        elements.push(element);
      });
    });
  }

  private createArticleNumberTextValue(text: string): SimpleTextValueVO {
    const textValue: SimpleTextValueVO = this.createTextValue(text);
    textValue.hAlignment = -1;
    return textValue;
  }

  private getDrillingHoleContainers(): ArticleContainer[] {
    const result: ArticleContainer[] = [];

    const mountingRailUsage: ArticleUsageVO = this.mountingRailHelper.getMountingRailUsage();
    const connectedUsageIds: string[] = ArticleUsageUtils.getAcceptingAreaIds(mountingRailUsage);
    for (const guid of connectedUsageIds) {
      const usage: ArticleUsageVO = this.projectModel.getArticleUsage(guid);
      if (this.articleUsageService.isDrillingHoleArticleUsage(usage)) {
        const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(guid);
        if (container) {
          result.push(container);
        }
      }
    }

    return result;
  }

  private createZeroLabel(startPoint: Vector3, level: number): LabelElement {
    const p1: Vector3 = startPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT));
    const position: Vector3 = MathUtils.getInterpolated(p1, startPoint, DimensionElement.ARROW_MULTIPLIER);
    const text: TextValueVO = this.createTextValue('0');
    position.x = position.x + DimensionUtils.getTextWidth(text as SimpleTextValueVO) / 2;
    position.z += (level + 1) * DimensionElement.LEVEL_OFFSET + DimensionUtils.getTextHeight(text as SimpleTextValueVO) / 2;

    const label: LabelElement = new LabelElement(text);
    label.position.set(position.x, position.y, position.z);

    return label;
  }

  private createCommonDimensionElement(startPoint: Vector3, endPoint: Vector3, level: number): DimensionElement {
    const commonDimension: DimensionElement = new DimensionElement(
      this.sceneManager,
      [
        startPoint,
        startPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT)),
        endPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT)),
        endPoint,
      ],
      this.createTextValue(this.formatDimension(startPoint.distanceTo(endPoint) / 10)),
      this.material,
      level + 1
    );
    return commonDimension;
  }


  private createStartEndDimensionElements(endDescriptors: DimensionDescriptor[]): DrillingHoleDimensionElement[] {
    const endDimensionElements: DrillingHoleDimensionElement[] = [];
    for (const dimensionDescriptor of endDescriptors) {
      const dimensionStartPoint: Vector3 = dimensionDescriptor.startPoint.clone();
      const dimension: DrillingHoleDimensionElement = this.getDrillingHoleElement(
        dimensionStartPoint,
        dimensionDescriptor,
        this.material,
        undefined,
        false,
        false
      );
      endDimensionElements.push(dimension);
    }

    return endDimensionElements;
  }

  private createImagePlaneElement(mountingRail: ArticleContainer, maxY: number): ImagePlaneElement {
    const bounds = this.projectDimensionsService.getContainerBounds([mountingRail]);

    let width = bounds.max.z - bounds.min.z + 2 * DrillingHolesLayerElementCreator.SCREENSHOT_PADDING;
    let height = bounds.max.y - bounds.min.y + 2 * DrillingHolesLayerElementCreator.SCREENSHOT_PADDING;
    const screenshot = this.screenShotService.orthogonalScreenshot(bounds, 'left', 700, 450, DrillingHolesLayerElementCreator.SCREENSHOT_PADDING);

    const element: ImagePlaneElement = new ImagePlaneElement(width, height, screenshot);
    const elementPosition = new Vector3(width / 2, maxY, 0);
    element.position.set(elementPosition.x, elementPosition.y, elementPosition.z);

    return element;
  }

  private createFrontViewElements(mountingRail: ArticleContainer): ILayerElement[] {
    const maxY = this.calculateMaxGlobalArticlePoint(mountingRail).y;
    const mountingRailProperties = mountingRail.articleUsage.infoProperties;
    const topWidth = PropertyUtils.getFloatPropertyValue(mountingRailProperties, ArticleUsageInfoPropertyDef.TOP_ARTICLE_DEPTH);
    const bottomWidth = PropertyUtils.getFloatPropertyValue(mountingRailProperties, ArticleUsageInfoPropertyDef.BOTTOM_ARTICLE_DEPTH);

    const bounds = this.projectDimensionsService.getContainerBounds([mountingRail]);

    const height = bounds.max.y - bounds.min.y;

    const {topStartX, topEndX, bottomStartX, bottomEndX, sideArrowX} = this.calculateArrowPositions(topWidth, bottomWidth);

    // we add +1 to each elements y except the imagePlane to avoid z-fighting
    const heightArrow = this.createVerticalArrow(
      new Vector3(sideArrowX, maxY + 1, height / 2),
      new Vector3(sideArrowX, maxY + 1, -height / 2));

    const label = this.createVerticalLineDimensionLabel(
      this.formatDimension(UnitConverter.fromTo('3d', 'mm', height)), sideArrowX + 30);
    label.position.y += 1;

    const topLabelZ = height / 2 + 20;
    const bottomLabelZ = -height / 2 - 130;

    const topArrow = this.createCommonDimensionElement(new Vector3(topStartX, maxY + 1, topLabelZ), new Vector3(topEndX, 1, topLabelZ), -1);
    const bottomArrow = this.createCommonDimensionElement(new Vector3(bottomStartX, maxY + 1, bottomLabelZ), new Vector3(bottomEndX, 1, bottomLabelZ), -1);

    return [
      topArrow,
      bottomArrow,
      ...heightArrow,
      label,
      this.createImagePlaneElement(mountingRail, maxY),
    ];
  }

  private calculateArrowPositions(topWidth: number, bottomWidth: number) {
    const padding = DrillingHolesLayerElementCreator.SCREENSHOT_PADDING;
    const halfTopBotDiff = Math.abs(topWidth - bottomWidth) / 2;
    const topLarger = topWidth > bottomWidth;

    const topStartX = topLarger ?
      topWidth + padding :
      padding + topWidth + halfTopBotDiff;

    const topEndX = topLarger ?
      padding :
      padding + halfTopBotDiff;

    const bottomStartX = topLarger ?
      halfTopBotDiff + bottomWidth + padding :
      bottomWidth + padding;

    const bottomEndX = topLarger ?
      halfTopBotDiff + padding :
      padding;

    const sideArrowX = (topLarger ? topStartX : bottomStartX) + 35;
    return {topStartX, topEndX, bottomStartX, bottomEndX, sideArrowX};
  }

  private collectDimensionDescriptors(startPoint: Vector3, endPoint: Vector3, articles: ArticleContainer[]): DimensionDescriptor[] {
    if (ArrayUtils.isEmpty(articles)) {
      return [];
    }

    const descriptors: DimensionDescriptor[] = [];
    let offset: number = 0;
    const originalStartPoint: Vector3 = startPoint.clone();
    let sizeEndPoint: Vector3 = startPoint.clone();
    const mountingRailVector: Vector3 = endPoint.clone().sub(startPoint);
    mountingRailVector.normalize();

    let index: number = 1;
    let descriptor: DimensionDescriptor;
    for (const article of articles) {
      let articleOffset: number = this.calculateOffset(article);
      const articleWidth: number = this.calculateWidth(article);
      let pointerOnRight: boolean = true;

      if (articleOffset > startPoint.x) {
        // If article position is left of rail start, move bullet marker to the right edge of the article.
        articleOffset = Math.abs(startPoint.x + (articleOffset - articleWidth / 2));
        offset = Math.abs(startPoint.x);
      } else if (articleOffset < endPoint.x) {
        // If article position is right of rail end, move bullet marker to the left edge of the article.
        articleOffset = articleOffset + articleWidth / 2;
      }

      const articleStartPosition: Vector3 = startPoint.clone().add(this.multiplyVector(mountingRailVector, Math.abs(offset)));
      const articleEndPosition: Vector3 = startPoint.clone().add(this.multiplyVector(mountingRailVector, Math.abs(articleOffset)));
      offset = articleOffset;

      descriptor = this.createDimensionDescriptor(
        articleStartPosition,
        articleEndPosition,
        false,
        this.createTextValue(this.formatDimension(originalStartPoint.distanceTo(articleEndPosition) / 10))
      );
      descriptor.index = index;
      descriptor.pointerOnRight = pointerOnRight;
      descriptors.push(descriptor);

      sizeEndPoint = articleEndPosition;
      index++;
    }

    if (endPoint.distanceTo(sizeEndPoint) >= DrillingHolesLayerElementCreator.SIZE_EPSILON) {
      descriptor = this.createDimensionDescriptor(sizeEndPoint, endPoint, false, this.createTextValue(''));
      descriptor.pointerRequired = false;
      descriptor.isOuterLabel = false;
      descriptors[descriptors.length] = descriptor;
    }

    return descriptors;
  }

  protected setMinMaxValues(dimensionDescriptor: DimensionDescriptor, startPoint: Vector3, endPoint: Vector3, textWidth: number): void {
    dimensionDescriptor.minX = startPoint.x;
    dimensionDescriptor.maxX = endPoint.x;
  }

  protected findMaxZValue(): number {
    let max: number = Number.MIN_VALUE;
    const boundingPoints: Vector3[] = this.findBoundingPointsInGlobal(this.mountingRailHelper.getMountingRailContainer());
    for (const point of boundingPoints) {
      max = Math.max(max, point.z);
    }
    return max + DrillingHolesLayerElementCreator.OFFSET_FROM_MOUNTING_RAIL;
  }

  protected createLayerElement(
    dimensionDescriptor: DimensionDescriptor,
    dimensionsLevels: Dictionary<number, DimensionMetrics>,
    createBackground: boolean = false
  ): ILayerElement {
    const material: MeshBasicMaterial = new MeshBasicMaterial({color: 0x000000});
    dimensionDescriptor.sizeLevel = !dimensionDescriptor.isOuterLabel
      ? 1
      : DimensionLevelMetricsUtil.findAvailableDimensionLevelByMetrics(dimensionsLevels, dimensionDescriptor.minX, dimensionDescriptor.maxX);
    if (dimensionDescriptor.sizeLevel === 1 && !dimensionDescriptor.pointerOnRight) {
      dimensionDescriptor.sizeLevel--;
    }

    if (!dimensionDescriptor.pointerRequired) {
      dimensionDescriptor.sizeLevel = DimensionLevelMetricsUtil.findFirstNotEmptyAvalibleSizeLevel(dimensionsLevels, dimensionDescriptor.minX);
    }

    const dimensionStartPoint: Vector3 = dimensionDescriptor.startPoint.clone();
    dimensionStartPoint.x = DimensionLevelMetricsUtil.getLastMaxXForLevel(dimensionsLevels, dimensionDescriptor.sizeLevel);

    return this.getDrillingHoleElement(
      dimensionStartPoint,
      dimensionDescriptor,
      material,
      this.createTextValue(String(dimensionDescriptor.index)),
      dimensionDescriptor.pointerRequired,
      dimensionDescriptor.pointerOnRight
    );
  }

  private articlePositionCompareFunction(articleA: ArticleContainer, articleB: ArticleContainer): number {
    let result: number = 0;

    const a: number = this.calculateOffset(articleA);
    const b: number = this.calculateOffset(articleB);

    if (a === b) {
      result = 0;
    } else if (a < b) {
      result = 1;
    } else {
      result = -1;
    }

    return result;
  }


  private getDrillingHoleElement(
    dimensionStartPoint: Vector3,
    dimensionDescriptor: DimensionDescriptor,
    material: MeshBasicMaterial,
    pointerText: SimpleTextValueVO | undefined,
    pointerRequired: boolean,
    pointerOnRight: boolean
  ): DrillingHoleDimensionElement {
    return new DrillingHoleDimensionElement(
      this.sceneManager,
      [
        dimensionStartPoint,
        dimensionStartPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT)),
        dimensionDescriptor.endPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT)),
        dimensionDescriptor.endPoint,
      ],
      dimensionDescriptor.text,
      material,
      dimensionDescriptor.sizeLevel,
      DrillingHolesLayerElementCreator.OFFSET_FROM_MOUNTING_RAIL - DrillingHolesLayerElementCreator.OFFSET_FROM_MOUNTING_RAIL / 2.5,
      pointerText,
      DimensionElement.LEVEL_OFFSET,
      dimensionDescriptor.fontSize,
      dimensionDescriptor.fontScale,
      dimensionDescriptor.arrowMultiplier,
      pointerRequired,
      pointerOnRight
    );
  }

  private changeArticlesVisibility(value: boolean): void {
    const mountingRailContainer: ArticleContainer = this.mountingRailHelper.getMountingRailContainer();
    if (mountingRailContainer) {
      const combinedArticles: ArticleContainer[] = this.articleContainerUtils.enumerateCombinedArticlesInclEndplates(mountingRailContainer);
      for (const articleContainer of combinedArticles) {
        if (ArticleUtils.isDrillingHole(articleContainer.article) || ArticleUtils.isMountingRail(articleContainer.article)) {
          continue;
        }
        articleContainer.visible = value;
      }
    }
  }
}
